import { useState, type FormEvent } from "react";
import { Button } from "@/components/ui/button";
import { setTenant, getLastSlug } from "@/lib/tenant";
import { type TenantId } from "@/lib/tenants";
import { track } from "@/lib/telemetry";

export function TenantPicker({ onPicked }: { onPicked: (slug: TenantId) => void }) {
  const [value, setValue] = useState(() => getLastSlug() ?? "");
  const [error, setError] = useState<string | null>(null);

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!value.trim()) {
      setError("Enter your workspace ID to continue.");
      return;
    }
    const validated = setTenant(value);
    if (!validated) {
      setError(
        "That workspace isn't recognized. Double-check with your admin or try again.",
      );
      return;
    }
    track("excel.tenant_picked", { tenant: validated });
    setError(null);
    onPicked(validated);
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-6 px-6 text-center">
      <img src="/logo.svg" alt="myai" width={64} height={64} />
      <div className="flex flex-col gap-2">
        <h1 className="text-lg font-semibold text-[var(--text-primary)]">
          Welcome to myai for Excel
        </h1>
        <p className="text-sm text-[var(--text-secondary)]">
          Enter your workspace ID to continue.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="flex w-full max-w-xs flex-col gap-2">
        <label
          htmlFor="workspace-id"
          className="text-left text-[11px] uppercase tracking-wide text-[var(--text-muted)]"
        >
          Workspace ID
        </label>
        <input
          id="workspace-id"
          type="text"
          autoFocus
          autoComplete="off"
          spellCheck={false}
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          placeholder="e.g. customer"
          aria-invalid={error != null}
          aria-describedby={error ? "workspace-id-error" : "workspace-id-help"}
          className="rounded-md border border-[var(--border-default)] bg-[var(--surface-base)] px-3 py-2 text-[15px] text-[var(--text-primary)] placeholder:text-[var(--text-muted)] focus:border-[var(--brand-accent)] focus:outline-none"
        />
        <p
          id="workspace-id-help"
          className="text-left text-[12px] text-[var(--text-muted)]"
        >
          Get this from your administrator.
        </p>
        {error && (
          <p
            id="workspace-id-error"
            role="alert"
            className="text-left text-[12px] text-[var(--status-error)]"
          >
            {error}
          </p>
        )}
        <Button type="submit" disabled={!value.trim()}>
          Continue
        </Button>
      </form>
    </div>
  );
}
